import {Component, ElementRef, inject, OnInit, ViewContainerRef, ChangeDetectionStrategy} from '@angular/core';
import {Router} from "@angular/router";
import {StateConfigService} from "../../../services/state/state-config.service";
import {ChatService} from "../service/chat.service";
import {
  faArrowDown,
  faCheck,
  faEllipsisVertical,
  faFile,
  faFolderOpen,
  faPenToSquare,
  faPlus,
  faSpinner,
  faThumbtack,
  faThumbtackSlash,
  faTrash,
  faXmark
} from "@fortawesome/pro-solid-svg-icons";
import {Tooltip} from "flowbite";
import {TemplatePortal} from "@angular/cdk/portal";
import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {NgClass} from '@angular/common';
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {FormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {DeleteChatDialogComponent} from './delete-chat-dialog/delete-chat-dialog.component';

@Component({
    selector: 'app-all-chats-view',
    templateUrl: './all-chats-view.component.html',
    styleUrls: ['./all-chats-view.component.scss'],
    host: {
      '(document:click)': 'onDocumentClick($event)'
    },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, FaIconComponent, FormsModule, BicDropdownComponent, BicDropdownTriggerDirective, BicDropdownElementComponent, BicButtonComponent]
})
export class AllChatsViewComponent implements OnInit{
  protected chatService = inject(ChatService);
  private router = inject(Router);
  private stateConfigService = inject(StateConfigService);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef = inject(ElementRef);
  private dialog = inject(MatDialog);

  hovering: boolean[] = [];
  dropdownOpen: boolean[] = [];

  private overlayRef!: OverlayRef;
  private multiplier = 0;
  isLoadingMore = false;
    onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Check if click is outside all dropdown areas
    for (let i = 0; i < this.dropdownOpen.length; i++) {
      if (this.dropdownOpen[i]) {
        const rowElement = this.elementRef.nativeElement.querySelector(`[data-chat-row="${i}"]`);
        if (rowElement && !rowElement.contains(target)) {
          this.closeDropdown(i);
          // Force hide the three dots when clicking outside
          this.hovering[i] = false;
        }
      }
    }
  }

  closeDropdown(index: number): void {
    this.dropdownOpen[index] = false;
    // Check if mouse is still hovering over the row
    const rowElement = this.elementRef.nativeElement.querySelector(`[data-chat-row="${index}"]`);
    if (rowElement && !rowElement.matches(':hover')) {
      this.hovering[index] = false;
    }
  }
  openDropdownAt(index: number): void {
  this.dropdownOpen[index] = true;
}

  // Add method to handle mouse leave
  onMouseLeave(index: number): void {
    // Only hide the icon if dropdown is not open
    if (!this.dropdownOpen[index]) {
      this.hovering[index] = false;
    }
  }

  async ngOnInit() {
    await this.chatService.listChats();
  }

  getChatList() {
    return this.chatService.threads
  }

  async loadMoreChats(): Promise<void> {
    if (this.isLoadingMore || !this.chatService.canLoadMoreChats()) return;

    this.isLoadingMore = true;
    try {
      await this.chatService.listChats(true);
    } finally {
      this.isLoadingMore = false;
    }
  }

  canLoadMore(): boolean {
    return this.chatService.threads.length > 0 && this.chatService.canLoadMoreChats();
  }

  isNewDate(index: number): boolean {
    if (index === 0) return true;
    if (index > this.chatService.threads.length - 1) return true;
    const currentDate = this.chatService.threads[index].createdOn.toISOString().split('T')[0];
    const previousDate = this.chatService.threads[index - 1].createdOn.toISOString().split('T')[0];
    return currentDate !== previousDate;
  }

  /** Die Chat-Liste gehört zum klassischen Chat; myCheck-Threads erscheinen hier nicht. */
  getCurrentChat() {
    return this.chatService.session('classic').currentChat
  }

  selectChat(chat: { id: string, topic: string, createdOn: Date }) {
    // Skip if this chatBIC is already selected
    if (this.getCurrentChat()?.id === chat.id) {
      return;
    }
    const session = this.chatService.session('classic');
    this.chatService.updateCurrentChat('classic', chat)
    session.BIM_Check = undefined;
    session.LOI_Check = undefined;
    session.selectedFiles = [];

    this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'chat', chat.id]).then(() => {
      this.chatService.getChatMessages('classic', chat.id);
    })
  }

  deleteChat(chat: { id: string, topic: string, createdOn: Date }) {
    const dialogRef = this.dialog.open(DeleteChatDialogComponent, {
      panelClass: 'rounded-corners-dialog',
      data: {mode: 'single', chatTopic: chat.topic},
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.chatService.deleteChat(chat.id).then(() => {
        if (this.getCurrentChat()?.id === chat.id) {
          this.chatService.resetChatState('classic');
          this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'chat']);
        }
      });
    });
  }

  deleteAllChats() {
    const dialogRef = this.dialog.open(DeleteChatDialogComponent, {
      panelClass: 'rounded-corners-dialog',
      data: {mode: 'all'},
    });

    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) return;

      // Copy the list to avoid concurrent modification
      const chatsToDelete = [...this.getChatList()];

      for (const chat of chatsToDelete) {
        await this.chatService.deleteChat(chat.id);
      }

      this.chatService.resetChatState('classic');
      this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'chat']);
    });
  }

  togglePinChat(chat: { id: string, topic: string, createdOn: Date }) {
    //this.chatService.togglePinChat(chat.id);
  }

  showTooltip(targetElId: string, triggerElId:string) {
    const targetEl = document.getElementById(targetElId);
    const triggerEl = document.getElementById(triggerElId);
    if (targetEl) {
      const tooltip =  new Tooltip(targetEl, triggerEl, {triggerType: 'hover', placement:'top'}, {id: targetElId, override: true});
      tooltip.show()
    }
  }

  onIconClick(event: Event, buttonId: string, dropdown: any) {
    const buttonElement = document.getElementById(buttonId) as HTMLElement;
    if (buttonElement) {
      dropdown.openDropdown(event, buttonElement, 'below');
    } else {
      console.error(`Element with ID ${buttonId} not found.`);
    }
  }

  closeAllChatsView() {
    this.chatService.chatHistoryExpanded = false;
  }

  openDropdown(event: Event, origin: HTMLElement, dropdown:any, direction: 'above' | 'below' = 'below') {
    event.stopPropagation();
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions([
        {
          originX: 'start',
          originY: direction === 'below' ? 'bottom' : 'top',
          overlayX: 'start',
          overlayY: direction === 'below' ? 'top' : 'bottom',
        }
      ]);

    this.overlayRef = this.overlay.create({positionStrategy});
    const portal = new TemplatePortal(dropdown, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.overlayRef.overlayElement.style.visibility = 'hidden';

    setTimeout(() => {
      const overlayWidth = this.overlayRef.overlayElement.offsetWidth;
      const buttonWidth = origin.offsetWidth;
      const updatedPositionStrategy = this.overlay.position()
        .flexibleConnectedTo(origin)
        .withPositions([
          {
            originX: 'start',
            originY: direction === 'below' ? 'bottom' : 'top',
            overlayX: 'start',
            overlayY: direction === 'below' ? 'top' : 'bottom',
            offsetX: (-overlayWidth + buttonWidth) * this.multiplier
          }
        ]);
      this.overlayRef.updatePositionStrategy(updatedPositionStrategy);
      this.overlayRef.overlayElement.style.visibility = 'visible';
    });
  }

  protected readonly faTrash = faTrash;
  protected readonly faFile = faFile;
  protected readonly faFolderOpen = faFolderOpen;

  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faPlus = faPlus;
  protected readonly faPenToSquare = faPenToSquare;
  renameChat: boolean[]=[];
  protected readonly faXmark = faXmark;
  protected readonly faCheck = faCheck;
  topicInputIsFocused: boolean = false;
  protected readonly faArrowDown = faArrowDown;
  protected readonly faSpinner = faSpinner;
  protected readonly faThumbtack = faThumbtack;
  protected readonly faThumbtackSlash = faThumbtackSlash;
}
